import { Button } from '@storeblocks/button';
import LinkList from '@storeblocks/link-list';
import { toString } from 'lodash';
import { type FC } from 'react';
import styled from 'styled-components';

import { useFetchFundHoldingsQuery } from '@/api/fundHoldings/fundHoldingsApi';
import { useFundListQuery } from '@/api/funds/fundsApi';
import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { InternalLink } from '@/components/InternalLink';
import { ConnectSurface } from '@/components/Pretzel/ConnectSurface';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { fmsWithTemplate } from '@/i18n/fmsWithTemplate';
import { toUrlFriendlySlug } from '@/pages/FundList/utils/toUrlFriendlySlug';
import { TrackingElement, useGetTrackId } from '@/tracking';

interface Props {
  portfolioGroupId?: string;
}

/**
 * Display a list of all the funds in the given portfolio group.
 */
export const EsgFundList: FC<Props> = ({ portfolioGroupId }) => {
  const texts = useTexts();
  const organizationCmId = useOrganizationCmId();
  const fundListQuery = useFundListQuery();
  const holdingsQuery = useFetchFundHoldingsQuery();
  const getTrackId = useGetTrackId();

  const holdingsForPortfolioGroup = holdingsQuery
    .data!.all.filter((holdings) =>
      // If portfolioGroupId is not given, return all holdings.
      portfolioGroupId ? holdings.portfolioGroupId === portfolioGroupId : true,
    )
    // Make the funds with the highest market value appear first.
    .sort((holdings) => holdings.marketValue) // Ascending sort.
    .reverse() // Make it descending.
    .map((holding) => holding.isin);

  const fundsForPortfolioGroup = fundListQuery.data!.funds.filter((fund) =>
    holdingsForPortfolioGroup.includes(fund.isin),
  );

  const firstThreeFunds = fundsForPortfolioGroup.slice(0, 3);

  if (fundsForPortfolioGroup.length === 0) {
    return (
      <StyledSurface>
        <WithGap gap="16">
          <h3>{texts.pages.esg.dataOnFundLevel.noFunds.title}</h3>

          <div>
            <Button
              to={`/${organizationCmId}/fund/fund-list`}
              as={InternalLink}
              variant="tertiary"
              iconRight="arrow_right"
              data-trackid={getTrackId(
                'go-to-fund-list-ad',
                TrackingElement.Link,
              )}
              data-trackid-source="esg-page"
            >
              {texts.pages.esg.dataOnFundLevel.noFunds.linkText}
            </Button>
          </div>

          <StyledImg
            src={`${window.config.storeblocksAssetsBaseUrl}/illustrations/sustainable-city.svg`}
          />
        </WithGap>
      </StyledSurface>
    );
  }

  return (
    <StyledSurface>
      <WithGap gap="16">
        <h3>{texts.pages.esg.dataOnFundLevel.title}</h3>

        <p className="font-body">
          {fmsWithTemplate(texts.pages.esg.dataOnFundLevel.widgetDescription, {
            actualFunds: toString(firstThreeFunds.length),
            fundsLength: toString(fundsForPortfolioGroup.length),
          })}
        </p>

        <LinkList>
          {firstThreeFunds.map((fund) => (
            <InternalLink
              key={fund.isin}
              to={`/${organizationCmId}/sustainability/esg/fund/${
                fund.isin
              }/${toUrlFriendlySlug(fund.name)}`}
              data-trackid={getTrackId('go-to-fund', TrackingElement.Link)}
              data-trackid-source="esg-page"
              data-trackid-isin={fund.isin}
              data-trackid-fund-name={fund.name}
            >
              {fund.name}
            </InternalLink>
          ))}
        </LinkList>

        {fundsForPortfolioGroup.length > firstThreeFunds.length && (
          <p className="font-body-demibold">
            {fmsWithTemplate(
              texts.pages.esg.dataOnFundLevel.thereAreMoreFunds,
              {
                amount: (
                  fundsForPortfolioGroup.length - firstThreeFunds.length
                ).toString(),
              },
            )}
          </p>
        )}
      </WithGap>

      <div>
        <Button
          to={`/${organizationCmId}/fund/fund-list`}
          as={InternalLink}
          variant="tertiary"
          iconRight="arrow_right"
          data-trackid={getTrackId('go-to-fund-list', TrackingElement.Link)}
          data-trackid-source="esg-page"
        >
          {texts.pages.esg.dataOnFundLevel.seeAllFunds}
        </Button>
      </div>
    </StyledSurface>
  );
};

const StyledSurface = styled(ConnectSurface)`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: space-between;
`;

const StyledImg = styled.img`
  height: auto;
  width: 400px;
  align-self: center;
`;
